import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ClipboardDocumentCheckIcon,
  UserIcon,
  CalendarDaysIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentArrowDownIcon,
  PlusIcon
} from '@heroicons/react/24/outline';

const AttendanceTracker = () => {
  // États
  const [date, setDate] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [showCheckinModal, setShowCheckinModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [attendanceTime, setAttendanceTime] = useState({
    checkin: formatTime(new Date()),
    checkout: '',
    notes: ''
  });

  // Données fictives pour les employés
  const mockEmployees = [
    {
      id: 1,
      name: 'Karim Benali',
      position: 'Directeur',
      department: 'management',
      photo: null,
      schedule: '08:30 - 17:30'
    },
    {
      id: 2,
      name: 'Samia Hadjri',
      position: 'Coach Fitness',
      department: 'coaching',
      photo: null,
      schedule: '09:00 - 18:00'
    },
    {
      id: 3,
      name: 'Yacine Rahmouni',
      position: 'Réceptionniste',
      department: 'reception',
      photo: null,
      schedule: '08:00 - 16:00'
    },
    {
      id: 4,
      name: 'Lina Cherif',
      position: 'Finance',
      department: 'finance',
      photo: null,
      schedule: '09:00 - 17:00'
    },
    {
      id: 5,
      name: 'Omar Mansouri',
      position: 'Technicien',
      department: 'maintenance',
      photo: null,
      schedule: '08:00 - 17:00'
    }
  ];

  // Données fictives pour les départements
  const departments = [
    { value: 'all', label: 'Tous les départements' },
    { value: 'management', label: 'Direction' },
    { value: 'reception', label: 'Accueil' },
    { value: 'coaching', label: 'Coaching' },
    { value: 'finance', label: 'Finance' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'cleaning', label: 'Nettoyage' }
  ];

  // Données fictives pour les présences
  const [attendanceData, setAttendanceData] = useState({
    '2024-05-15': {
      1: { checkin: '08:15', checkout: '17:45', status: 'present', notes: '' },
      2: { checkin: '09:05', checkout: '18:15', status: 'present', notes: '' },
      3: { checkin: '08:10', checkout: '16:05', status: 'present', notes: '' },
      4: { checkin: '09:30', checkout: '17:10', status: 'late', notes: 'Retard de 30 minutes' },
      5: { checkin: '', checkout: '', status: 'absent', notes: 'Congé maladie' }
    }
  });

  // Filtrer les employés
  const filteredEmployees = mockEmployees.filter(employee => {
    const matchesSearch = employee.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         employee.position.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDepartment = departmentFilter === 'all' || employee.department === departmentFilter;
    return matchesSearch && matchesDepartment;
  });

  // Formatage de la date
  function formatDate(date) {
    return date.toISOString().split('T')[0];
  }

  // Formatage de l'heure
  function formatTime(date) {
    return date.toTimeString().substring(0, 5);
  }

  // Navigation entre les dates
  const goToNextDay = () => {
    const nextDay = new Date(date);
    nextDay.setDate(nextDay.getDate() + 1);
    setDate(nextDay);
  };

  const goToPreviousDay = () => {
    const prevDay = new Date(date);
    prevDay.setDate(prevDay.getDate() - 1);
    setDate(prevDay);
  };

  const goToToday = () => {
    setDate(new Date());
  };

  // Gérer l'enregistrement de présence
  const handleCheckinSubmit = () => {
    const currentDate = formatDate(date);
    
    // Créer ou mettre à jour la date dans les données de présence
    if (!attendanceData[currentDate]) {
      attendanceData[currentDate] = {};
    }
    
    // Mettre à jour la présence pour l'employé sélectionné
    setAttendanceData(prevData => ({
      ...prevData,
      [currentDate]: {
        ...prevData[currentDate],
        [selectedEmployee.id]: {
          checkin: attendanceTime.checkin,
          checkout: attendanceTime.checkout,
          status: attendanceTime.checkin ? (isLate(attendanceTime.checkin, selectedEmployee) ? 'late' : 'present') : 'absent',
          notes: attendanceTime.notes
        }
      }
    }));
    
    // Fermer la modal
    setShowCheckinModal(false);
    setSelectedEmployee(null);
  };

  // Vérifier si l'employé est en retard
  const isLate = (checkinTime, employee) => {
    if (!checkinTime || !employee.schedule) return false;
    
    const scheduledStart = employee.schedule.split(' - ')[0];
    const [scheduledHour, scheduledMinute] = scheduledStart.split(':').map(Number);
    const [checkinHour, checkinMinute] = checkinTime.split(':').map(Number);
    
    if (checkinHour > scheduledHour) return true;
    if (checkinHour === scheduledHour && checkinMinute > scheduledMinute + 5) return true;
    
    return false;
  };

  // Obtenir le statut pour un employé et une date donnés
  const getAttendanceStatus = (employeeId) => {
    const currentDate = formatDate(date);
    if (!attendanceData[currentDate] || !attendanceData[currentDate][employeeId]) {
      return { status: 'pending', data: null };
    }
    
    return { 
      status: attendanceData[currentDate][employeeId].status,
      data: attendanceData[currentDate][employeeId]
    };
  };

  // Statistiques de présence pour la date actuelle
  const getAttendanceStats = () => {
    const currentDate = formatDate(date);
    let present = 0;
    let absent = 0;
    let late = 0;
    
    if (attendanceData[currentDate]) {
      mockEmployees.forEach(employee => {
        const attendance = attendanceData[currentDate][employee.id];
        if (attendance) {
          if (attendance.status === 'present') present++;
          else if (attendance.status === 'absent') absent++;
          else if (attendance.status === 'late') late++;
        } else {
          // Si pas de données pour aujourd'hui mais la date est dans le passé
          const today = new Date();
          if (date < today && today.toDateString() !== date.toDateString()) {
            absent++;
          }
        }
      });
    }
    
    return { present, absent, late, pending: mockEmployees.length - (present + absent + late) };
  };

  // Formater la date pour l'affichage
  const formatDateForDisplay = (date) => {
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric'
    });
  };

  const stats = getAttendanceStats();

  return (
    <div className="space-y-6">
      {/* Navigation de date */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <div className="flex items-center gap-3">
          <button 
            onClick={goToPreviousDay}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <ChevronLeftIcon className="h-5 w-5 text-white" />
          </button>
          
          <div className="text-center">
            <div className="text-xl font-bold text-white">
              {formatDateForDisplay(date)}
            </div>
            <div className="text-white/60 text-sm mt-1">
              {date.toDateString() === new Date().toDateString() ? 'Aujourd\'hui' : ''}
            </div>
          </div>
          
          <button 
            onClick={goToNextDay}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <ChevronRightIcon className="h-5 w-5 text-white" />
          </button>
        </div>
        
        <div className="flex gap-2">
          <button 
            onClick={goToToday}
            className="px-4 py-2 bg-white/10 rounded-xl hover:bg-white/20 transition-colors text-white flex items-center gap-2"
          >
            <CalendarDaysIcon className="h-4 w-4" />
            Aujourd'hui
          </button>
          
          <button className="px-4 py-2 bg-white/10 rounded-xl hover:bg-white/20 transition-colors text-white flex items-center gap-2">
            <DocumentArrowDownIcon className="h-4 w-4" />
            Exporter
          </button>
        </div>
      </div>
      
      {/* Statistiques du jour */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Présents</p>
              <p className="text-2xl font-bold text-white mt-2">{stats.present}</p>
              <p className="text-green-400 text-xs mt-1">
                {((stats.present / mockEmployees.length) * 100).toFixed(0)}% des employés
              </p>
            </div>
            <div className="p-3 rounded-full bg-green-500/20">
              <CheckCircleIcon className="h-6 w-6 text-green-400" />
            </div>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Absents</p>
              <p className="text-2xl font-bold text-white mt-2">{stats.absent}</p>
              <p className="text-red-400 text-xs mt-1">
                {((stats.absent / mockEmployees.length) * 100).toFixed(0)}% des employés
              </p>
            </div>
            <div className="p-3 rounded-full bg-red-500/20">
              <XCircleIcon className="h-6 w-6 text-red-400" />
            </div>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Retards</p>
              <p className="text-2xl font-bold text-white mt-2">{stats.late}</p>
              <p className="text-yellow-400 text-xs mt-1">
                {((stats.late / mockEmployees.length) * 100).toFixed(0)}% des employés
              </p>
            </div>
            <div className="p-3 rounded-full bg-yellow-500/20">
              <ClockIcon className="h-6 w-6 text-yellow-400" />
            </div>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">En attente</p>
              <p className="text-2xl font-bold text-white mt-2">{stats.pending}</p>
              <p className="text-blue-400 text-xs mt-1">
                {((stats.pending / mockEmployees.length) * 100).toFixed(0)}% des employés
              </p>
            </div>
            <div className="p-3 rounded-full bg-blue-500/20">
              <ArrowPathIcon className="h-6 w-6 text-blue-400" />
            </div>
          </div>
        </motion.div>
      </div>
      
      {/* Filtres et recherche */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-white/50" />
            <input
              type="text"
              placeholder="Rechercher un employé..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Department Filter */}
          <div className="flex items-center space-x-2">
            <FunnelIcon className="h-5 w-5 text-white/50" />
            <select
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {departments.map((department) => (
                <option key={department.value} value={department.value}>
                  {department.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      
      {/* Liste des employés avec leur statut de présence */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-full">
            <thead className="bg-white/5">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                  Employé
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                  Département
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                  Horaire
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                  Arrivée
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                  Départ
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-white/70 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10 bg-white/5">
              {filteredEmployees.map((employee) => {
                const attendance = getAttendanceStatus(employee.id);
                return (
                  <tr key={employee.id} className="hover:bg-white/10 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {employee.photo ? (
                          <img src={employee.photo} alt={employee.name} className="h-10 w-10 rounded-full" />
                        ) : (
                          <div className="h-10 w-10 bg-gradient-to-br from-blue-500/30 to-purple-500/30 rounded-full flex items-center justify-center">
                            <UserIcon className="h-5 w-5 text-white/70" />
                          </div>
                        )}
                        <div className="ml-4">
                          <div className="text-sm font-medium text-white">{employee.name}</div>
                          <div className="text-sm text-white/60">{employee.position}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-white/70">
                        {departments.find(d => d.value === employee.department)?.label}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-white/70">
                        {employee.schedule}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm ${attendance.data?.checkin ? 'text-white' : 'text-white/40'}`}>
                        {attendance.data?.checkin || '—'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm ${attendance.data?.checkout ? 'text-white' : 'text-white/40'}`}>
                        {attendance.data?.checkout || '—'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {attendance.status === 'present' && (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-500/20 text-green-400">
                          Présent
                        </span>
                      )}
                      {attendance.status === 'absent' && (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-500/20 text-red-400">
                          Absent
                        </span>
                      )}
                      {attendance.status === 'late' && (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-500/20 text-yellow-400">
                          Retard
                        </span>
                      )}
                      {attendance.status === 'pending' && (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-500/20 text-blue-400">
                          En attente
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button 
                          onClick={() => {
                            setSelectedEmployee(employee);
                            setAttendanceTime({
                              checkin: attendance.data?.checkin || formatTime(new Date()),
                              checkout: attendance.data?.checkout || '',
                              notes: attendance.data?.notes || ''
                            });
                            setShowCheckinModal(true);
                          }}
                          className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                          title="Marquer présence"
                        >
                          <ClipboardDocumentCheckIcon className="h-4 w-4" />
                        </button>
                        {attendance.status !== 'pending' && (
                          <button 
                            className="p-2 bg-white/10 text-white/70 rounded-lg hover:bg-white/20 transition-colors"
                            title="Voir détails"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Modal d'enregistrement de présence */}
      {showCheckinModal && selectedEmployee && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowCheckinModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 max-w-md w-full"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">
                Marquer la présence
              </h2>
              <button
                onClick={() => setShowCheckinModal(false)}
                className="p-2 text-white/60 hover:text-white transition-colors"
              >
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="mb-6 flex items-center">
              <div className="h-16 w-16 bg-gradient-to-br from-blue-500/30 to-purple-500/30 rounded-full flex items-center justify-center">
                <UserIcon className="h-8 w-8 text-white/70" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-white">
                  {selectedEmployee.name}
                </h3>
                <p className="text-white/60 text-sm">
                  {selectedEmployee.position} • {departments.find(d => d.value === selectedEmployee.department)?.label}
                </p>
              </div>
            </div>
            
            <form onSubmit={(e) => { e.preventDefault(); handleCheckinSubmit(); }} className="space-y-4">
              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">
                  Heure d'arrivée
                </label>
                <input
                  type="time"
                  value={attendanceTime.checkin}
                  onChange={(e) => setAttendanceTime({...attendanceTime, checkin: e.target.value})}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">
                  Heure de départ
                </label>
                <input
                  type="time"
                  value={attendanceTime.checkout}
                  onChange={(e) => setAttendanceTime({...attendanceTime, checkout: e.target.value})}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">
                  Notes
                </label>
                <textarea
                  value={attendanceTime.notes}
                  onChange={(e) => setAttendanceTime({...attendanceTime, notes: e.target.value})}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 h-24"
                  placeholder="Ajoutez des notes (optionnel)"
                ></textarea>
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setShowCheckinModal(false)}
                  className="px-4 py-2 bg-white/10 text-white rounded-xl border border-white/20 hover:bg-white/20 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all flex items-center gap-2"
                >
                  <CheckCircleIcon className="h-5 w-5" />
                  Enregistrer
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default AttendanceTracker; 